import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import type {
  AdminUserDetailOutput,
  TAccountAuditAction,
  TAdminUser,
  TStaffAuditAction,
} from '@schemas'

import { useAddShowStaff, useRemoveShowStaff } from '@/api/hooks'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { STATUS_LABELS } from '@/lib/show-status'
import { STAFF_ROLE_LABELS, type StaffRole } from '@/data/staff-roles'
import { VISIBLE_ROLES } from '@/components/shows/ShowStaffRoster'
import { Button } from '@/components/ui/button'
import { ConfirmDialog, formatDate } from './account-components'

type ShowAccess = AdminUserDetailOutput['showAccess'][number]
type AssignableShow = AdminUserDetailOutput['assignableShows'][number]

function roleLabel(role: string) {
  return STAFF_ROLE_LABELS[role as StaffRole]?.singular ?? role
}

/**
 * Why the account can open each show: its linked person's staff assignments. Adding or removing
 * a role here is the same staff change Setup → Staff makes, and takes effect on the next request.
 */
export function ShowAccessSection({
  user,
  access,
  assignableShows,
  isLoading,
}: {
  user: TAdminUser
  access: ShowAccess[]
  assignableShows: AssignableShow[]
  isLoading: boolean
}) {
  const removeStaff = useRemoveShowStaff()
  const [removing, setRemoving] = useState<ShowAccess | null>(null)
  const [adding, setAdding] = useState(false)

  return (
    <div className="space-y-2">
      <p className="text-sm">
        {user.isAdmin
          ? 'As an administrator this account can open every show. Its staff roles below still apply if administrator access is removed.'
          : user.person
            ? `This account has ${user.person.name}'s staff roles.`
            : 'Link a person to give this account show roles.'}
      </p>
      {isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading show access…
        </p>
      ) : user.person && access.length === 0 ? (
        <p className="text-sm text-muted-foreground">Not staff in any show.</p>
      ) : (
        <ul className="divide-y rounded-md border text-sm">
          {access.map(entry => (
            <li key={entry.assignmentId} className="flex items-center justify-between gap-3 p-2">
              <span className="min-w-0">
                <Link
                  to={`/${entry.show.slug}/setup?section=staff`}
                  className="font-medium underline"
                >
                  {entry.show.name}
                </Link>{' '}
                <span className="text-muted-foreground">
                  (
                  {STATUS_LABELS[entry.show.status as keyof typeof STATUS_LABELS] ??
                    entry.show.status}
                  )
                </span>
                : {roleLabel(entry.role)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove ${roleLabel(entry.role)} in ${entry.show.name}`}
                onClick={() => setRemoving(entry)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      {user.person ? (
        adding ? (
          <AddShowRoleForm
            personId={user.person.id}
            shows={assignableShows}
            held={access}
            onDone={() => setAdding(false)}
          />
        ) : (
          <Button type="button" variant="outline" onClick={() => setAdding(true)}>
            Add a show role
          </Button>
        )
      ) : null}
      {removing ? (
        <ConfirmDialog
          title="Remove show role"
          confirmLabel="Remove role"
          pending={removeStaff.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await removeStaff.mutateAsync({
                showId: removing.show.id,
                assignmentId: removing.assignmentId,
              })
              toast.success('Show role removed.')
            } catch (error) {
              toast.error(parseApiErrorMessage(error))
            } finally {
              setRemoving(null)
            }
          }}
        >
          {user.person?.name ?? 'This person'} stops being {roleLabel(removing.role)} in{' '}
          {removing.show.name}, here and in Setup → Staff. Open sessions lose that access on their
          next request.
        </ConfirmDialog>
      ) : null}
    </div>
  )
}

/** Only shows this installation operates are offered; a role the person already holds is not. */
function AddShowRoleForm({
  personId,
  shows,
  held,
  onDone,
}: {
  personId: string
  shows: AssignableShow[]
  held: ShowAccess[]
  onDone: () => void
}) {
  const showFieldId = useId()
  const roleFieldId = useId()
  const addStaff = useAddShowStaff()
  const [showId, setShowId] = useState('')
  const [role, setRole] = useState<StaffRole | ''>('')
  const heldRoles = new Set(held.filter(entry => entry.show.id === showId).map(entry => entry.role))
  const canAdd = showId && role && !heldRoles.has(role)

  const add = async () => {
    try {
      if (!canAdd) return
      await addStaff.mutateAsync({ showId, payload: { role, personId } })
      toast.success('Show role added.')
      onDone()
    } catch (error) {
      toast.error(parseApiErrorMessage(error))
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border p-2">
      <div className="space-y-1">
        <label htmlFor={showFieldId} className="block text-xs font-medium">
          Show
        </label>
        <select
          id={showFieldId}
          className="h-9 max-w-56 rounded-md border bg-background px-3 text-sm"
          value={showId}
          onChange={event => setShowId(event.target.value)}
        >
          <option value="">{shows.length ? 'Choose a show' : 'No shows to add to'}</option>
          {shows.map(show => (
            <option key={show.id} value={show.id}>
              {show.name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label htmlFor={roleFieldId} className="block text-xs font-medium">
          Role
        </label>
        <select
          id={roleFieldId}
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={role}
          onChange={event => setRole(event.target.value as StaffRole | '')}
        >
          <option value="">Choose a role</option>
          {VISIBLE_ROLES.map(value => (
            <option key={value} value={value} disabled={heldRoles.has(value)}>
              {STAFF_ROLE_LABELS[value].singular}
              {heldRoles.has(value) ? ' (already held)' : ''}
            </option>
          ))}
        </select>
      </div>
      <Button type="button" disabled={!canAdd || addStaff.isPending} onClick={add}>
        Add role
      </Button>
      <Button type="button" variant="ghost" onClick={onDone} disabled={addStaff.isPending}>
        Cancel
      </Button>
    </div>
  )
}

const ACTION_LABELS: Record<TAccountAuditAction | TStaffAuditAction, string> = {
  'user.invite': 'Invited',
  'user.create': 'Created with a password',
  'user.setup.complete': 'Set up the account from its link',
  'user.password.reset': 'Reset the password from a link',
  'user.password.change': 'Changed the password',
  'user.password.set': 'Password set by an administrator',
  'user.setup_link.issue': 'Setup link created',
  'user.reset_link.issue': 'Password reset link created',
  'user.setup_link.email': 'Setup link emailed',
  'user.reset_link.email': 'Password reset link emailed',
  'user.setup_link.request': 'Setup link requested',
  'user.reset_link.request': 'Password reset link requested',
  'user.email.change': 'Email changed',
  'user.admin.grant': 'Administrator access granted',
  'user.admin.revoke': 'Administrator access removed',
  'user.disable': 'Disabled',
  'user.enable': 'Enabled',
  'user.sessions.revoke': 'Signed out of all devices',
  'user.person.link': 'Linked to a person',
  'user.person.unlink': 'Person link removed',
  'user.invite.revoke': 'Invitation deleted',
  'show.staff.create': 'Show role added',
  'show.staff.update': 'Show role changed',
  'show.staff.delete': 'Show role removed',
}

/** The account's newest changes and who made them. */
export function AccountHistorySection({
  history,
  isLoading,
}: {
  history: AdminUserDetailOutput['history']
  isLoading: boolean
}) {
  if (isLoading)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading history…
      </p>
    )
  if (history.length === 0)
    return <p className="text-sm text-muted-foreground">No recorded changes.</p>
  return (
    <ol className="space-y-1 text-sm">
      {history.map(event => (
        <li key={event.id}>
          <span className="text-muted-foreground">{formatDate(event.createdAt)}</span> ·{' '}
          {ACTION_LABELS[event.action as keyof typeof ACTION_LABELS] ?? event.action}
          {event.detail ? `: ${event.detail}` : ''} · by {event.actor}
        </li>
      ))}
    </ol>
  )
}
